import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Carousel, Card } from "react-bootstrap";
import LumenNavbar from "../../layout/LumenNavbar";
import NewFooter from "../../layout/NewFooter";
import { s3_url } from "common/constants";
import LumenButton from "../../ui/LumenButton";
import "./NewPreparations.css";

type PrepGroup = {
  title: string;
  hours: string;
  price: string;
  desc: string;
};

const GROUPS: PrepGroup[] = [
  {
    title: "MATEMATIKA A — VIŠA RAZINA",
    hours: "50 sati",
    price: "525 €",
    desc: "25 termina po 2 sunčana sata",
  },
  {
    title: "MATEMATIKA B — OSNOVNA RAZINA",
    hours: "42 sata",
    price: "441 €",
    desc: "21 termin po 2 sunčana sata",
  },
  {
    title: "HRVATSKI JEZIK",
    hours: "42 sata",
    price: "441 €",
    desc: "21 termin po 2 sunčana sata",
  },
];

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size)
    result.push(arr.slice(i, i + size));
  return result;
}

const NewPreparations: React.FC = () => {
  const [cardsPerSlide, setCardsPerSlide] = useState(3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 576) setCardsPerSlide(1);
      else if (w < 992) setCardsPerSlide(2);
      else setCardsPerSlide(3);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  const slides = useMemo(() => chunk(GROUPS, cardsPerSlide), [cardsPerSlide]);

  return (
    <>
      <LumenNavbar />
      <div className="preparations prep-page">
        <section className="prep-hero">
          <div
            className="prep-hero-bg"
            style={{ backgroundImage: `url(${s3_url}/pripreme/1.webp)` }}
            aria-label="Pripreme - hero"
          />
        </section>

        <section className="prep-hero-content">
          <Container>
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <h1 className="title-shadow d-md-none red-title text-center mt-0">
                  PRIPREME
                </h1>

                <p className="prep-intro">
                  Lumen@home je web platforma za pripreme ispita državne mature.
                  Osmislila ju je Karmen Dasović, profesorica matematike i
                  fizike te vlasnica škole za poduke Lumen, koju uspješno vodi
                  već 30 godina. Ljubav prema poučavanju odvela ju je u brojne
                  projekte, pa i u virtualni svijet. Kroz Lumen@home omogućuje
                  vam da učite učinkovito, iz udobnosti vlastitog doma.
                </p>

                <LumenButton
                  to="/prijava-pripreme-drzavna-matura"
                  className="prep-lumen-button prep-hero-cta"
                >
                  <span className="d-inline d-sm-none">PRIJAVI SE</span>
                  <span className="d-none d-sm-inline">
                    PRIJAVI SE NA PRIPREME
                  </span>
                </LumenButton>
              </Col>

              <Col lg={5} className="text-center">
                <img
                  src={`${s3_url}/homepage/lumenko.webp`}
                  className="pp-lumenko-hero-plain"
                  alt="Lumenko"
                  loading="lazy"
                />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="prep-band">
          <Container>
            <div className="band-copy-100">
              <h1 className="title-shadow">PRIPREME ZA MATURU BEZ STRESA</h1>
              <p className="exact">
                Vama maturantima je posljednja godina srednjoškolskog
                obrazovanja veoma bitna, ali i veoma stresna i teška. Često u
                danu nemate dovoljno vremena usvajati sve nastavne sadržaje
                predmeta u školi, pripremati se za ispite državne mature, baviti
                se sportom, a opet nastojati raditi i sve ono što vas veseli i
                što volite. Upravo za vas s previše obaveza u danu Lumen@home je
                idealno rješenje. Nećete gubiti vrijeme na put do naše škole za
                poduke, mi ćemo doći u vaš dom i pomoći vam s učenjem.
              </p>
            </div>
          </Container>

          <div className="band-two-images">
            <img src={`${s3_url}/pripreme/2.webp`} alt="Pripreme - rad" />
            <img
              src={`${s3_url}/pripreme/3.webp`}
              alt="Pripreme - nastavnica"
            />
          </div>
        </section>

        <section className="prep-how">
          <Container>
            <Row className="align-items-center g-5">
              <Col lg={12}>
                <h1 className="title-shadow text-center red-title mb-4">
                  KAKO ĆE SE PROVODITI PRIPREME NA LUMEN@HOME?
                </h1>
              </Col>
              <Col lg={7}>
                <p className="preparations-terms-text">
                  Pripreme za ispite državne mature održavat će se u fiksnim
                  terminima, u trajanju od dva školska sata svakog tjedna. Kada
                  se na našoj platformi prijavite za pripreme iz određenog
                  predmeta, bit ćete dodani u grupu na platformi Lumen@home.
                  Tamo ćete pronaći sve obavijesti i materijale koje je
                  nastavnik pripremio za vas. Ogledne primjere naših materijala
                  možeš pogledati{" "}
                  <a
                    href={`${s3_url}/samples/ogledni_primjerak_mat1.pdf`}
                    target="_blank"
                  >
                    ovdje
                  </a>{" "}
                  i{" "}
                  <a
                    href={`${s3_url}/samples/ogledni_primjerak_mat2.pdf`}
                    target="_blank"
                  >
                    ovdje
                  </a>
                  .
                </p>
              </Col>
              <Col lg={5}>
                <div className="red-icon-circle">
                  <img
                    src={`${s3_url}/homepage/knjige.webp`}
                    alt="Books"
                    className="icon-in-circle"
                    loading="lazy"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="prep-card">
          <Container>
            <div className="laptop-card gradient simple-rounded">
              <Row className="align-items-center g-3">
                <Col md={4} className="text-center">
                  <img
                    src={`${s3_url}/pripreme/5.webp`}
                    alt="Laptop"
                    className="laptop-img"
                    loading="lazy"
                  />
                </Col>
                <Col md={8}>
                  <p className="card-text exact">
                    Pripreme se provode putem videopoziva, u realnom vremenu,
                    tako da ćeš imati mogućnost razgovarati s nastavnikom i
                    postavljati pitanja ukoliko nastavni sadržaji ne budu jasni.
                  </p>
                </Col>
              </Row>
            </div>
          </Container>
        </section>

        <section className="prep-teachers">
          <Container>
            <h1 className="title-shadow text-center red-title my-5">
              PRIPREME UZ PROVJERENE PROFESORE
            </h1>
            <Row className="align-items-center g-4 mt-5 mb-5">
              <Col lg={{ span: 8, offset: 2 }}>
                <p className="teachers-text exact text-center">
                  Pripreme vode naši dugogodišnji suradnici s višegodišnjim
                  iskustvom rada u školi, kao i u vođenju priprema za ispite
                  državne mature. Svaki od njih odabran je zbog stručnosti,
                  posvećenosti i razumijevanja potreba maturanata.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        <div
          className="prep-big-image"
          style={{ backgroundImage: `url(${s3_url}/pripreme/6.webp)` }}
          role="img"
          aria-label="Nastava"
        />

        <section className="prep-steps">
          <Container>
            <h1 className="title-shadow text-center red-title mb-5">
              Kako se prijaviti na pripreme na Lumen@home?
            </h1>

            <div className="steps-grid my-5">
              <div className="step step-1">
                <span className="num">1</span>
                <img
                  src={`${s3_url}/homepage/lumenko.webp`}
                  alt=""
                  className="step-lumenko"
                  loading="lazy"
                />
                <p className="exact">
                  Prvo morate ispuniti prijavnicu za pripreme koja se nalazi na
                  crvenim gumbima na naslovnoj stranici ili na izbornoj traci
                  platforme Prijava Priprema.
                </p>
              </div>

              <div className="step step-2">
                <span className="num">2</span>
                <img
                  src={`${s3_url}/homepage/lumenko.webp`}
                  alt=""
                  className="step-lumenko"
                  loading="lazy"
                />
                <p className="exact">
                  Ubrzo će vam biti dodijeljena grupa za predmete na koje ste se
                  prijavili
                </p>
              </div>

              <div className="step step-3">
                <span className="num">3</span>
                <img
                  src={`${s3_url}/homepage/lumenko.webp`}
                  alt=""
                  className="step-lumenko"
                  loading="lazy"
                />
                <p className="exact">
                  Tada ćete kreirati vlastiti račun kojim ćete se prijavljivati
                  u aplikaciju Lumen@home.
                </p>
              </div>

              <div className="step step-4">
                <span className="num">4</span>
                <img
                  src={`${s3_url}/homepage/lumenko.webp`}
                  alt=""
                  className="step-lumenko"
                  loading="lazy"
                />
                <p className="exact">
                  Tamo vas čekaju obavijesti i radni materijali.
                </p>
              </div>
            </div>

            <div className="cta-pill bigger my-5">
              Želimo održati kvalitetu našeg rada i u virtualnom okruženju, zato
              će grupe biti male – od 10 do 14 učenika.
            </div>
          </Container>
        </section>

        <section className="prep-groups">
          <Container>
            <h2 className="title-shadow text-center mb-4">
              Grupe priprema za maturu na Lumen@home
            </h2>

            <Carousel
              interval={8000}
              indicators={false}
              variant="dark"
              className="prep-carousel"
            >
              {slides.map((slide, idx) => (
                <Carousel.Item key={idx}>
                  <Row className="justify-content-center gx-4 gy-4">
                    {slide.map((g) => (
                      <Col
                        key={g.title}
                        xs={12}
                        md={cardsPerSlide === 1 ? 12 : 6}
                        lg={4}
                      >
                        <Card className="group-card white shadow-sm">
                          <Card.Header className="gc-header text-center">
                            <div className="gc-title">{g.title}</div>
                          </Card.Header>
                          <Card.Body className="text-center">
                            <div className="gc-sep" />
                            <p className="mb-2">{g.hours}</p>
                            <p className="mb-3">{g.desc}</p>
                            <p className="price">{g.price}</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Carousel.Item>
              ))}
            </Carousel>
          </Container>
        </section>

        <section className="prep-groups-cta">
          <Container className="text-center">
            <p className="big-note">
              Nemoj previše čekati, grupe se brzo pune!
            </p>
            <p className="big-note">
              Grupe priprema za maturu na Lumen@home započinju s radom u prvom
              tjednu veljače.
            </p>
            <LumenButton
              to="/prijava-pripreme-drzavna-matura"
              className="prep-lumen-button"
            >
              PRIJAVI SE
            </LumenButton>
          </Container>
        </section>
      </div>
      <NewFooter />
    </>
  );
};

export default NewPreparations;
